import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import SectionEyebrow from "./SectionEyebrow";
import BrandButton from "./BrandButton";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const viewport = { once: false, amount: 0.18 };

const models = [
  {
    eyebrow: "Dedicated Development Support",
    title: "Flexible Hours",
    desc: "Work with our experienced developers for ongoing development, integrations, technical support, and product improvements.",
    features: ["Feature development", "API integrations", "Ongoing support"],
    cta: "Discuss Your Requirements",
    featured: true,
  },
  {
    eyebrow: "Project-Based Development",
    title: "Custom Quote",
    desc: "For clearly defined projects with specific requirements and timelines. Ideal for web applications, AI automation, and custom solutions.",
    features: ["Custom development", "AI & automation", "End-to-end delivery"],
    cta: "Get a Project Quote",
    featured: false,
  },
];

const EngagementModels = () => (
  <section
    id="how-we-work"
    className="mk-services-surface py-24 lg:py-28"
  >
    <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={stagger}
        className="mx-auto mb-12 max-w-4xl text-center"
      >
        <SectionEyebrow variants={reveal}>How We Work</SectionEyebrow>
        <motion.h2
          variants={reveal}
          className="mt-4 font-display text-3xl font-semibold leading-tight text-slate-900 [text-wrap:balance] sm:text-4xl lg:text-5xl"
        >
          Flexible Ways to Work with Our Team
        </motion.h2>
        <motion.p
          variants={reveal}
          className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600"
        >
          Whether you need ongoing support or a complete project build, we offer
          flexible engagement models to match your goals.
        </motion.p>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={stagger}
        className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:gap-8"
      >
        {models.map((m) => (
         <motion.article
  key={m.title}
  variants={reveal}
  whileHover={{
    y: -2,
    borderColor: "#8B4AFA",
    boxShadow: "0 8px 20px -12px #8B4AFA55",
  }}
  transition={{ duration: 0.2, ease: "easeOut" }}
  className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white px-6 py-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.04),0_6px_16px_-4px_rgba(15,23,42,0.03)] transition-[border-color,box-shadow] duration-200 sm:px-12 lg:px-16 ${
              m.featured
                ? "border-[#8B4AFA] shadow-[0_20px_48px_-28px_rgba(139,74,250,0.55)]"
                : "border-slate-200"
            }`}
          >
            <span
              aria-hidden="true"
              className={`absolute inset-x-0 top-0 h-1 ${
                m.featured ? "bg-[#8B4AFA]" : "bg-[#8B4AFA]/25"
              }`}
            />

            {m.featured && (
              <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 overflow-hidden">
                <div className="absolute right-[-34px] top-[18px] w-[130px] rotate-45 bg-[#8B4AFA] py-1 text-center text-[9px] font-bold uppercase tracking-[0.12em] text-white">
                  Most Popular
                </div>
              </div>
            )}

            <p className="text-sm font-semibold text-[#6e3fc7]">{m.eyebrow}</p>
            <h3 className="mt-3 font-display text-3xl font-semibold text-slate-900 sm:text-4xl">
              {m.title}
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
              {m.desc}
            </p>

            <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
              {m.features.map((f) => (
                <li
                  key={f}
                  className="flex items-center gap-2 text-sm text-slate-700"
                >
                  <span
                   className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-110 ${
                      m.featured
                        ? "bg-[#8B4AFA] text-white"
                        : "bg-[#8a4afa1c] text-[#8B4AFA]"
                    }`}
                  >
                    <FaCheck aria-hidden="true" className="h-2 w-2" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-8">
              <BrandButton
                as={Link}
                to="/contact"
                variant={m.featured ? "primary" : "secondary"}
                className="mk-cta-button--full"
              >
                {m.cta}
              </BrandButton>
            </div>
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);

export default EngagementModels;